import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Peças do Premium North (design-system/north/MASTER.md) usadas por Vendas,
 * Clientes e Contatos. Ficam aqui até subirem para o kit comum (bento/ui.tsx):
 * a faixa de indicadores (um cartão só, dividido em células, filete dourado) e
 * o selo de situação (cor fixa por significado, sempre com texto).
 */

export type TomSelo = 'ok' | 'alerta' | 'erro' | 'destaque' | 'andamento' | 'neutro';

const TONS: Record<TomSelo, string> = {
  ok: 'bg-ok-fundo text-ok',
  alerta: 'bg-alerta-fundo text-alerta',
  erro: 'bg-erro-fundo text-erro',
  destaque: 'bg-destaque-fundo text-destaque',
  andamento: 'bg-[#EEF1F6] text-primary',
  neutro: 'bg-muted text-muted-foreground',
};

/** Selo com ponto: "Confirmada", "Pendente", "Em atraso"… */
export const Selo = ({
  tom,
  children,
  title,
  className,
}: {
  tom: TomSelo;
  children: ReactNode;
  title?: string;
  className?: string;
}) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-[4px] px-2 py-0.5 text-[11.5px] font-semibold',
      'before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-current',
      TONS[tom],
      className,
    )}
  >
    {children}
  </span>
);

/** Etiqueta pequena sem ponto (origem da venda, estratégia). */
export const Etiqueta = ({
  children,
  title,
  className,
}: {
  children: ReactNode;
  title?: string;
  className?: string;
}) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center gap-0.5 whitespace-nowrap rounded-[4px] border border-border px-1.5 py-px text-[10.5px] font-semibold text-foreground/80',
      className,
    )}
  >
    {children}
  </span>
);

export type Indicador = {
  rotulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  icone?: ReactNode;
};

/**
 * A faixa de indicadores: um cartão com filete dourado, células divididas.
 * No celular as células empilham uma por linha.
 */
export const FaixaIndicadores = ({ itens, className }: { itens: Indicador[]; className?: string }) => (
  <div
    className={cn(
      // gap-px sobre o fundo da borda: as divisórias saem certas em qualquer
      // número de colunas, sem regra de borda por posição.
      'cartao border-t-ouro grid grid-cols-1 gap-px overflow-hidden bg-border',
      itens.length === 2 && 'sm:grid-cols-2',
      itens.length === 3 && 'sm:grid-cols-3',
      itens.length >= 4 && 'sm:grid-cols-2 lg:grid-cols-4',
      className,
    )}
  >
    {itens.map((item) => (
      <div key={item.rotulo} className="min-w-0 bg-card px-4 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
            {item.rotulo}
          </p>
          {item.icone ? (
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#EEF1F6] text-primary [&_svg]:h-3.5 [&_svg]:w-3.5">
              {item.icone}
            </span>
          ) : null}
        </div>
        <p className="mt-1.5 truncate text-[23px] font-bold leading-none tracking-[-0.02em] tabular-nums text-marinho">
          {item.valor}
        </p>
        {item.detalhe ? (
          <p className="mt-1.5 truncate text-[11.5px] text-muted-foreground">{item.detalhe}</p>
        ) : null}
      </div>
    ))}
  </div>
);

/** Botões de escolha em pílula (Segmento, Motivo, Último lance…), mesmo comportamento de antes. */
export const pilulaFiltro = (ativo: boolean) =>
  cn(
    'inline-flex h-7 cursor-pointer items-center rounded-full border px-3 text-xs font-semibold transition-colors',
    ativo
      ? 'border-marinho bg-marinho text-white'
      : 'border-dashed border-[#D5DBE3] bg-card text-foreground/80 hover:bg-muted',
  );
